import React from "react";
import "./src/global.css";
import "vanilla-cookieconsent/dist/cookieconsent.css";
import theme from "./src/theme";
import { ThemeProvider } from "@mui/material/styles";
import { Amplify } from "aws-amplify";
import { fetchAuthSession } from "aws-amplify/auth";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import utc from "dayjs/plugin/utc";
import { Provider, useDispatch } from "react-redux";
import store from "./src/state/store";
import { useMount } from "react-use";
import { getUserData } from "./src/state/actions/user";
import { setAuthInit, setAuthLoader } from "./src/state/actions/base";
import { initializeGA, handleGACookies } from "./src/helpers/analytics";
import { CookieStorage } from "aws-amplify/utils";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";

const RootComponent = ({ element }) => {
  const dispatch = useDispatch();

  useMount(async () => {
    if (element?.props?.location) return; // prevent from running twice... location is available when running for Head component

    const { tokens } = await fetchAuthSession();

    if (tokens) {
      dispatch(setAuthLoader(true));

      await dispatch(getUserData(tokens));
    }

    dispatch(setAuthInit(true));
    dispatch(setAuthLoader(false));
  });

  return <>{element}</>;
};

export const wrapRootElement = ({ element }) => {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <RootComponent element={element} />
      </ThemeProvider>
    </Provider>
  );
};

export const onClientEntry = () => {
  dayjs.extend(utc);
  dayjs.extend(duration);

  // Configure token storage BEFORE Amplify.configure()
  if (process.env.GATSBY_AUTH_CUSTOM_DOMAIN) {
    cognitoUserPoolsTokenProvider.setKeyValueStorage(
      new CookieStorage({
        domain: process.env.GATSBY_AUTH_CUSTOM_DOMAIN,
        path: "/",
        expires: 365,
        secure: true,
      })
    );
  }

  Amplify.configure({
    Auth: {
      Cognito: {
        region: process.env.GATSBY_AUTH_REGION,
        userPoolId: process.env.GATSBY_AUTH_USER_POOL_ID,
        userPoolClientId: process.env.GATSBY_AUTH_USER_POOL_CLIENT_ID,
        loginWith: {
          oauth: {
            domain: process.env.GATSBY_AUTH_COGNITO_DOMAIN,
            scopes: ["openid", "profile", "email"],
            redirectSignIn: [`${window.location.origin}/user/redirect-signin`],
            redirectSignOut: [
              `${window.location.origin}/user/redirect-signout`,
            ],
            responseType: "code",
            providers: ["GitHub", "Google"],
          },
        },
      },
    },
    API: {
      REST: {
        users: {
          endpoint: process.env.GATSBY_USER_API,
        },
      },
    },
  });
};

export const onInitialClientRender = () => {
  // Import vanilla-cookieconsent dynamically
  import("vanilla-cookieconsent").then((cc) => {
    // Make CookieConsent API available globally
    if (typeof window !== "undefined") {
      window.CookieConsent = cc;
    }

    cc.run({
      guiOptions: {
        consentModal: {
          layout: "box inline",
          position: "bottom left",
        },
        preferencesModal: {
          layout: "box",
          position: "right",
        },
      },

      categories: {
        necessary: {
          enabled: true,
          readOnly: true,
        },
        analytics: {},
      },

      language: {
        default: "en",
        translations: {
          en: {
            consentModal: {
              title: "We use cookies",
              description:
                "We use cookies to enhance your browsing experience, analyze site traffic, and understand where our audience is coming from. By clicking 'Accept all', you consent to our use of cookies.",
              acceptAllBtn: "Accept all",
              acceptNecessaryBtn: "Reject all",
              showPreferencesBtn: "Manage preferences",
            },
            preferencesModal: {
              title: "Cookie preferences",
              acceptAllBtn: "Accept all",
              acceptNecessaryBtn: "Reject all",
              savePreferencesBtn: "Save preferences",
              closeIconLabel: "Close",
              sections: [
                {
                  title: "Cookie usage",
                  description:
                    "We use cookies to ensure the basic functionalities of the website and to enhance your online experience. You can choose for each category to opt-in/out whenever you want.",
                },
                {
                  title: "Strictly necessary cookies",
                  description:
                    "These cookies are essential for the proper functioning of the website. Without these cookies, the website would not work properly.",
                  linkedCategory: "necessary",
                },
                {
                  title: "Analytics cookies",
                  description:
                    "These cookies collect information about how you use the website, which pages you visited and which links you clicked on. All of the data is anonymized and cannot be used to identify you.",
                  linkedCategory: "analytics",
                },
              ],
            },
          },
        },
      },

      onConsent: ({ cookie }) => {
        if (cookie.categories.includes("analytics")) {
          initializeGA();
        }
      },

      onChange: ({ cookie, changedCategories }) => {
        if (changedCategories.includes("analytics")) {
          if (cookie.categories.includes("analytics")) {
            initializeGA();
          } else {
            handleGACookies();
          }
        }
      },
    });
  });
};
